import { Node } from "@tiptap/core";

/**
 * `@`-mentions of a document, a person, or a THINKER.
 *
 * The third kind is the addition: a workspace's thinkers are mentionable in prose, ranked
 * above people, because in a document that is being worked on a thinker is most often who you
 * mean. Selecting one runs no membership, permission or invite work — a thinker is not a
 * person being given access, it is a name being written.
 *
 * Two kinds render differently and that difference is the point:
 *  - a **document** mention is a LINK, because following it is the whole reason to write one;
 *  - a **person** or **thinker** mention is a chip — a name, not a destination.
 *
 * The markdown is the platform's existing form: `@[Label](mention://<mentionId>/<type>/<modelId>)`.
 * Thinkers round-trip through it for free because the parse rule reads the type segment as an
 * opaque word.
 *
 * ⚠ The NODE serialises the link part only — `[Label](mention://…)` — because the `@` in that
 * form is a separate literal text token, not part of the link. It survives the round trip on
 * its own, so emitting one here too produced `@@[Label](…)`. Measured, not assumed: the
 * round-trip test caught the doubling.
 */

export type MentionType = "document" | "user" | "thinker";

const MENTION_HREF = /^mention:\/\/([^/]+)\/([a-z]+)\/([^/?#]+)$/;

export const MentionNode = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      /** Identity of this MENTION, not of the thing mentioned — two mentions of one person differ. */
      mentionId: { default: "" },
      type: { default: "user" as MentionType },
      /** The mentioned entity's own id. */
      modelId: { default: "" },
      label: { default: "" },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-mention-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const type = String(HTMLAttributes.type ?? "user");
    const label = String(HTMLAttributes.label ?? "");
    const attrs = {
      "data-mention-id": String(HTMLAttributes.mentionId ?? ""),
      "data-mention-type": type,
      "data-model-id": String(HTMLAttributes.modelId ?? ""),
      class: "docit-mention",
    };
    return type === "document"
      ? ["a", { ...attrs, href: `/documents/${String(HTMLAttributes.modelId ?? "")}` }, label]
      : ["span", attrs, `@${label}`];
  },

  renderText({ node }) {
    const type = String(node.attrs.type ?? "user");
    const label = String(node.attrs.label ?? "");
    // A document mention reads as its title; a person or thinker reads as a handle.
    return type === "document" ? label : `@${label}`;
  },

  markdownTokenName: "mention",

  renderMarkdown(node) {
    const attrs = (node.attrs ?? {}) as {
      mentionId?: string;
      type?: string;
      modelId?: string;
      label?: string;
    };
    return `[${attrs.label ?? ""}](mention://${attrs.mentionId ?? ""}/${attrs.type ?? "user"}/${attrs.modelId ?? ""})`;
  },
});

/**
 * `@[Label](mention://id/type/modelId)` → a mention node, or null.
 *
 * Exported rather than inlined because the inline parse path and the tests both need it, and
 * because the href grammar is the one thing here that must not drift from the platform's.
 */
export function parseMentionHref(
  href: string,
  label: string,
): { mentionId: string; type: MentionType; modelId: string; label: string } | null {
  const match = MENTION_HREF.exec(href);
  if (!match?.[1] || !match[2] || !match[3]) {
    return null;
  }
  const type = match[2];
  return {
    mentionId: match[1],
    // An unknown future type renders as a chip rather than being dropped — the same choice the
    // widget registry makes for an unknown widget: lose the styling, never the content.
    type: type === "document" || type === "thinker" ? type : "user",
    modelId: match[3],
    label,
  };
}
